import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { DuplicateDecision, PendingCreations } from "@/components/matter/CreationRecovery";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MotionButton, SPRING, EASE, DURATION } from "@/motion";
import { TextField, SelectField, AreaField } from "@/components/common/Field";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { uid } from "@/lib/id";
import { ConflictCheckStep } from "./ConflictCheckStep";

const STEPS = ["Basic", "Court", "Parties", "Conflict", "Dates", "Assignment", "Financial"];
const TYPES = ["Writ Petition", "Civil Suit", "Criminal Complaint", "Statutory Appeal", "Arbitration", "Consumer Complaint", "Original Application", "Public Interest Litigation", "Original Petition"];
const AREAS = ["Constitutional / Writ", "Civil — Property", "Service Law", "Negotiable Instruments", "Commercial / Contract", "Taxation / Customs", "Banking / DRT", "Arbitration", "Consumer", "Succession"];
const COURTS = ["High Court of Andhra Pradesh", "High Court for the State of Telangana", "Supreme Court of India", "Senior Civil Judge, Kakinada", "Judicial Magistrate of First Class, Rajahmundry", "District Consumer Commission, Visakhapatnam", "CESTAT, Hyderabad", "Debts Recovery Tribunal, Visakhapatnam", "Arbitral Tribunal (Sole Arbitrator)"];
const STAGES = ["Filing", "Admission", "Notice Issued", "Counter Filed", "Pleadings", "Evidence", "Arguments", "Interim Stay", "Final Hearing", "Drafting"];
const FEE_TYPES = ["Fixed", "Per Appearance", "Per Draft", "Stage Based", "Hourly", "Monthly Retainer", "Custom Arrangement"];

export function NewMatterDialog({ open, onOpenChange, defaultClientId }) {
  const { db, scope, loadWorkspace, ccore } = useApp();
  const requestId = useRef(crypto.randomUUID());
  const [busy, setBusy] = useState(false), [failure, setFailure] = useState(""), [duplicate, setDuplicate] = useState(null), [decision, setDecision] = useState({});
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [check, setCheck] = useState(null);
  const [form, setForm] = useState({
    title: "", type: "Writ Petition", practiceArea: "Constitutional / Writ", clientId: defaultClientId || "",
    description: "", court: COURTS.includes(ccore?.primaryForumId) ? ccore.primaryForumId : "High Court of Andhra Pradesh", state: ccore?.homeState || "Andhra Pradesh", district: "",
    caseNumber: "", stage: "Filing", judge: "", efilingNumber: "", jurisdiction: ccore?.primaryForumId || "",
    petitioner: "", respondent: "", opponentCounsel: "",
    filingDate: "", nextHearing: "", limitationDate: "",
    leadId: scope.dirId || "", priority: "Medium", tags: "",
    matterValue: "", feeArrangement: "Fixed", estimatedFee: "", advanceReceived: "",
  });

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (["clientId", "petitioner", "respondent", "opponentCounsel", "signatoryName", "instrument"].includes(k)) setCheck(null); };
  const clientOpts = useMemo(() => db.clients.map((c) => ({ value: c.id, label: c.name })), [db.clients]);
  const userOpts = useMemo(() => db.users.map((u) => ({ value: u.id, label: u.name })), [db.users]);

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.title.trim()) e.title = "Matter title is required";
      if (!form.clientId) e.clientId = "Select a client";
    }
    if (step === 1 && !form.court) e.court = "Court is required";
    if (step === 3) {
      if (!check) e.conflict = "Run the conflict check before continuing";
      else if (check.status !== "CLEAR" && !check.override) e.conflict = "Conflict findings must be cleared or overridden by a partner";
    }
    setErrors(e);
    if (e.conflict) toast.error(e.conflict);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) { setDir(1); setStep((s) => Math.min(s + 1, STEPS.length - 1)); } };
  const back = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    if (!validateStep() || busy) return;
    setBusy(true); setFailure("");
    const parties = [form.petitioner && { name: form.petitioner, role: "Petitioner" }, form.respondent && { name: form.respondent, role: "Respondent" }].filter(Boolean);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/matter-creations`, { requestId: requestId.current, matter: {
        ...form, ...decision, parties, assignedTo: [form.leadId], caseNumber: form.caseNumber || "UNNUMBERED",
        matterValue: Number(form.matterValue) || 0, estimatedFee: Number(form.estimatedFee) || 0,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        conflictCheckId: check?.id, authority: { signatoryName: form.signatoryName || "", instrument: form.instrument || "" },
      } });
      await loadWorkspace();
      setDone(true);
      toast.success("Matter and linked records saved.");
      setTimeout(() => { onOpenChange(false); setStep(0); setDone(false); requestId.current = crypto.randomUUID(); navigate(`/matters/${data.matter.id}`); }, 560);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code?.startsWith("DUPLICATE_")) setDuplicate(d);
      setFailure(typeof d === "string" ? d : d?.message || "Creation was interrupted. Retry safely with the same request, or resume from Matters after reloading.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-matter-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New Matter</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 items-center gap-1.5">
              <motion.div
                animate={{
                  backgroundColor: i <= step ? "#9C6F2A" : "#EDE8DF",
                  color: i <= step ? "#ffffff" : "#606773",
                  scale: i === step ? 1.08 : 1,
                }}
                transition={SPRING.micro}
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold"
              >{i + 1}</motion.div>
              <span className={cn("hidden text-xs sm:inline", i === step ? "font-medium text-ink" : "text-ink-muted")}>{s}</span>
              {i < STEPS.length - 1 && (
                <div className="relative h-px flex-1 bg-line">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-copper"
                    initial={false}
                    animate={{ width: i < step ? "100%" : "0%" }}
                    transition={{ duration: DURATION.normal, ease: EASE.standard }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="nyk-scroll max-h-[52vh] overflow-x-hidden overflow-y-auto py-2 pr-1">
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={step}
              custom={dir}
              initial={{ x: dir * 24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: dir * -24, opacity: 0 }}
              transition={{ duration: DURATION.normal, ease: EASE.enter }}
            >
          {step === 0 && (
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Matter Title" required value={form.title} onChange={(v) => set("title", v)} error={errors.title} className="col-span-2" testid="matter-title-input" placeholder="e.g. Kurella Govindu v. APEPDCL" />
              <SelectField label="Matter Type" value={form.type} onChange={(v) => set("type", v)} options={TYPES} />
              <SelectField label="Practice Area" value={form.practiceArea} onChange={(v) => set("practiceArea", v)} options={AREAS} />
              <SelectField label="Client" required value={form.clientId} onChange={(v) => set("clientId", v)} options={clientOpts} error={errors.clientId} testid="matter-client-select" />
              <TextField label="Internal Matter ID" value={form.internalId} onChange={(v) => set("internalId", v)} placeholder="Optional" />
              <AreaField label="Description" value={form.description} onChange={(v) => set("description", v)} className="col-span-2" />
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="Court" required value={form.court} onChange={(v) => set("court", v)} options={COURTS} error={errors.court} className="col-span-2" />
              <TextField label="State" value={form.state} onChange={(v) => set("state", v)} />
              <TextField label="District" value={form.district} onChange={(v) => set("district", v)} />
              <TextField label="Case Number" value={form.caseNumber} onChange={(v) => set("caseNumber", v)} placeholder="e.g. WP 23821/2026" />
              <SelectField label="Stage" value={form.stage} onChange={(v) => set("stage", v)} options={STAGES} />
              <TextField label="Judge / Bench" value={form.judge} onChange={(v) => set("judge", v)} />
              <TextField label="E-filing Number" value={form.efilingNumber} onChange={(v) => set("efilingNumber", v)} />
              <TextField label="Jurisdiction" value={form.jurisdiction} onChange={(v) => set("jurisdiction", v)} className="col-span-2" />
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Petitioner / Plaintiff / Complainant" value={form.petitioner} onChange={(v) => set("petitioner", v)} className="col-span-2" />
              <TextField label="Respondent / Defendant / Accused" value={form.respondent} onChange={(v) => set("respondent", v)} className="col-span-2" />
              <TextField label="Opponent Counsel" value={form.opponentCounsel} onChange={(v) => set("opponentCounsel", v)} className="col-span-2" />
            </div>
          )}
          {step === 3 && <ConflictCheckStep form={form} set={set} check={check} setCheck={setCheck} />}
          {step === 4 && (
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Filing Date" type="date" value={form.filingDate} onChange={(v) => set("filingDate", v)} />
              <TextField label="Next Hearing" type="date" value={form.nextHearing} onChange={(v) => set("nextHearing", v)} />
              <TextField label="Limitation Date" type="date" value={form.limitationDate} onChange={(v) => set("limitationDate", v)} />
            </div>
          )}
          {step === 5 && (
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="Lead Advocate" value={form.leadId} onChange={(v) => set("leadId", v)} options={userOpts} />
              <SelectField label="Priority" value={form.priority} onChange={(v) => set("priority", v)} options={["High", "Medium", "Low"]} />
              <TextField label="Tags (comma separated)" value={form.tags} onChange={(v) => set("tags", v)} className="col-span-2" placeholder="Urgent, Electricity" />
            </div>
          )}
          {step === 6 && (
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Matter Value (₹)" type="number" value={form.matterValue} onChange={(v) => set("matterValue", v)} />
              <SelectField label="Fee Arrangement" value={form.feeArrangement} onChange={(v) => set("feeArrangement", v)} options={FEE_TYPES} />
              <TextField label="Estimated Fee (₹)" type="number" value={form.estimatedFee} onChange={(v) => set("estimatedFee", v)} />
              <p className="col-span-2 text-xs text-ink-2" data-testid="matter-financial-estimate-notice">Estimates only. Record actual advances in Billing after creating this matter.</p>
            </div>
          )}
            </motion.div>
          </AnimatePresence>
        </div>

        <PendingCreations />
        {failure && <p role="alert" className="text-sm text-danger" data-testid="matter-creation-error">{failure}</p>}
        <DuplicateDecision issue={duplicate} values={decision} onChange={setDecision} />
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button variant="ghost" onClick={step === 0 ? () => onOpenChange(false) : back}>
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {step < STEPS.length - 1 ? (
            <MotionButton onClick={next} data-testid="matter-next-btn">Continue</MotionButton>
          ) : (
            <MotionButton onClick={submit} loading={busy && !done} success={done} successLabel="Matter Created" disabled={busy} data-testid="matter-submit-btn">
              {busy ? "Saving linked records…" : "Create Matter"}
            </MotionButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
